import { getStatusBooleanValue, headingStructureRule } from '@wix/a11y-rules';
import { useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { rescanTriggerGroupIdAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { HeadingStructureRuleFixer } from '../../components/fixers/heading-structure-fixer/heading-structure-rule-fixer';
import { HeadingStructureRuleNoHeadings } from '../../components/fixers/heading-structure-fixer/heading-structure-rule-no-headings';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { STUDIO_COMP_REF_TYPE } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { getHeadingStructureElementCounts } from '../../utils/get-heading-structure-element-counts';
import { getPageTypeForBi } from '../shared/bi-helpers';
import { shouldUpdateRuleStatus } from '../shared/helpers';
import { getUpdatedHeadingStructureElementsFromState } from './get-updated-heading-structure-elements-from-state';
const ruleChecker = headingStructureRule.checker;
export const useHeadingStructureRule = () => {
  const {
    t
  } = useTranslation();
  const navigateToPage = useNavigation();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    getRuleState,
    panelState,
    setRuleState,
    getGroupData
  } = usePanelStateAtom();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const setRescanTriggerGroupId = useSetAtom(rescanTriggerGroupIdAtom);
  const getHeadingStructure = useCallback(_ref => {
    var _getGroupData, _ruleState$state;
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const {
      result,
      ruleId,
      metadata
    } = ruleExecutionResult;
    const breakpointId = (metadata == null ? void 0 : metadata.breakpointId) ?? STUDIO_COMP_REF_TYPE;
    const analysisState = (_getGroupData = getGroupData(routerId ?? pageId)) == null ? void 0 : _getGroupData.analysisState;
    const {
      allHeadings,
      status
    } = result;
    const ruleState = getRuleState(routerId ?? pageId, ruleId);
    const initialHtmlStrings = allHeadings ?? [];
    const htmlStrings = getUpdatedHeadingStructureElementsFromState(initialHtmlStrings, ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.htmlStrings, panelState.globalSectionComponentsDataMap);
    const {
      failedReasons,
      failedHeadings
    } = ruleChecker({
      htmlStrings: htmlStrings.map(el => ({
        ...el,
        id: el.uiElementId
      }))
    });
    const {
      total: elementCount,
      fail: failedElementCount
    } = getHeadingStructureElementCounts(htmlStrings, failedHeadings, failedReasons);
    const uiStatus = failedReasons.length === 0 ? RuleListStatus.passed : RuleListStatus.failed;
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, ruleId, {
        status: uiStatus
      });
    }
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: getStatusBooleanValue(status) ? 'accessibility-wizard.rules-list.heading-definition.description' : 'accessibility-wizard.rules-list.heading-definition.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-heading-structure-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount,
            failedElementCount
          });
        },
        title: getStatusBooleanValue(status) ? t('accessibility-wizard.rules-list.issue-resolved') : ''
      }),
      elementCount,
      failedElementCount,
      fixer: FixerType.Custom,
      fixerProps: {
        customFixer: htmlStrings.length === 0 ? /*#__PURE__*/React.createElement(HeadingStructureRuleNoHeadings, {
          pageId: routerId ?? pageId
        }) : /*#__PURE__*/React.createElement(HeadingStructureRuleFixer, {
          a11yWizardAdapter: a11yWizardAdapter,
          breakpointId: breakpointId,
          editorHost: editorHost,
          failedHeadings: failedHeadings,
          failedReasons: failedReasons,
          htmlStrings: htmlStrings,
          pageId: routerId ?? pageId
        })
      },
      id: `${isCrossPage ? 'cross-page-' : ''}${routerId ?? pageId}-${ruleId}`,
      label: t('accessibility-wizard.rules-list.heading-definition.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount,
          failedElementCount,
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(metadata == null ? void 0 : metadata.itemType, isCrossPage),
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId, routerId);
          setRescanTriggerGroupId(isCrossPage ? pageId : undefined);
        } else {
          setRescanTriggerGroupId(undefined);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount,
          failedElementCount,
          status: uiStatus
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus
    };
  }, [a11yWizardAdapter, biLogger, currentTaskInfo, editorHost, getGroupData, getRuleState, navigateToPage, openSelectedArticle, panelState.globalSectionComponentsDataMap, setCurrentTaskInfo, setRescanTriggerGroupId, setRuleState, t]);
  return {
    getHeadingStructure
  };
};
//# sourceMappingURL=use-heading-structure-rule.js.map